<template>
	<EventLayout :event="event" :subtitle="transChoice('enrollments.count', enrollments.total)">
		<template #actions>
			<Link v-if="canCreate" :as="PrimaryButton" :color="Color.Green" :title="trans('general.create')" :href="route('events.enrollments.create', event.id)">
				<i class="fa-solid fa-plus"/>
			</Link>
			<PrimaryButton :color="Color.Indigo" :title="trans('general.filter.title')" @click.stop="showFilters">
				<i class="fa-solid fa-filter"/>
			</PrimaryButton>
			<PrimaryButton :color="Color.Purple" :title="trans('general.export')" @click.stop="downloadBySearch">
				<i class="fa-solid fa-file-export"/>
			</PrimaryButton>
			<SecondaryButton :color="Color.Fuchsia" :title="trans('events.enrollments.mailing.download.all')" @click.stop="downloadAll">
				<i class="fa-solid fa-download"/>
			</SecondaryButton>
			<SecondaryButton :color="Color.Blue" :title="trans('events.enrollments.counters')" @click.stop="showModal('show-counters-modal')">
				<i class="fa-solid fa-arrow-up-1-9"/>
			</SecondaryButton>
		</template>
		<Modal id="show-counters-modal" overflow no-footer>
			<template #title>{{ trans('events.enrollments.counters') }}</template>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<p class="grow text-sm">{{ trans('enrollments.enrollees.types.general') }}</p>
				<p>{{ event.general_enrollees_count }}</p>
			</div>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<p class="grow text-sm">{{ trans('enrollments.enrollees.types.international') }}</p>
				<p>{{ event.international_enrollees_count }}</p>
			</div>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<p class="grow text-sm">{{ trans('enrollments.enrollees.types.representative') }}</p>
				<p>{{ event.representative_enrollees_count }}</p>
			</div>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<p class="grow text-sm">{{ trans('enrollments.enrollees.types.organizer') }}</p>
				<p>{{ event.organizer_enrollees_count }}</p>
			</div>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<p class="grow text-sm">{{ trans('enrollments.enrollees.types.workgroup') }}</p>
				<p>{{ event.workgroup_enrollees_count }}</p>
			</div>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<p class="grow text-sm">{{ trans('enrollments.enrollees.types.board') }}</p>
				<p>{{ event.board_enrollees_count }}</p>
			</div>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<p class="grow text-sm">{{ trans('enrollments.enrollees.types.dinosaur') }}</p>
				<p>{{ event.dinosaur_enrollees_count }}</p>
			</div>
			<div class="px-3 py-1 rounded flex items-center gap-3 text-gray-800 bg-gray-200 dark:text-gray-100 dark:bg-gray-900">
				<span class="grow text-sm">{{ trans('enrollments.status.accredited.title') }}</span>
				<span>{{ event.accredited_count }}</span>
			</div>
		</Modal>
		<Modal id="show-filter-modal">
			<template #title>{{ trans('general.filter.title') }}</template>
			<LabeledObject>
				<template #label>{{ trans('enrollments.user') }}</template>
				<input v-model="searchTerm" type="text">
			</LabeledObject>
			<LabeledObject>
				<template #label>{{ trans('general.filter.by') }} {{ trans('general.filter.college.name') }}</template>
				<select v-model="searchCollegeId">
					<option :value="null">{{ trans('general.filter.college.all') }}</option>
					<option v-for="college in colleges" :value="college.id">{{ college.name }}</option>
				</select>
			</LabeledObject>
			<div class="grid grid-cols-2 lg:grid-cols-3 gap-3">
				<Checkbox v-for="status in EnrollmentStatus" :value="status" :label="trans(`enrollments.status.${status}.title`)" :checked="searchStatuses.includes(status)" @checked="addStatus(status)" @unchecked="removeStatus(status)"/>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Blue" @click.stop="filter">
					<i class="fa-solid fa-search mr-2"/>
					{{ trans('general.filter.title') }}
				</PrimaryButton>
			</template>
		</Modal>
		<Pagination :resource="enrollments"/>
		<Empty v-if="enrollments.data.length === 0"/>
		<CardGrid v-else tight>
			<EnrollmentItem
				v-for="enrollment in enrollments.data"
				:key="`enrollment-${enrollment.id}`"
				:enrollment="enrollment"
				:can-mark="canMark"
				:can-update="canUpdate"
				:can-delete="canDelete"
				@deleted="remove"
			/>
		</CardGrid>
		<Pagination :resource="enrollments"/>
	</EventLayout>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {Link, useForm} from '@inertiajs/vue3'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Modal from '@/Components/UI/Modals/Modal.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import {trans, transChoice} from 'laravel-vue-i18n'
import Pagination from '@/Components/UI/Pagination.vue'
import {type College} from '@/Types/Settings/College'
import {closeDropdownList, showModal} from '@/Helpers/general'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import {type Enrollment} from '@/Types/Events/Enrollment'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import Checkbox from '@/Components/UI/Checkboxes/Checkbox.vue'
import {EnrollmentStatus} from '@/Types/Enums/EnrollmentStatus'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import EnrollmentItem from '@/Components/Events/Enrollments/Enrollment.vue'

const props = defineProps<{
	event:Event,
	canMark:boolean,
	canCreate:boolean,
	canUpdate:boolean,
	canDelete:boolean,
	searchTerm:string,
	searchCollegeId:number,
	colleges:Array<College>,
	enrollments:PaginatedModel<Enrollment>,
	searchStatuses:Array<EnrollmentStatus>,
}>()

const event:Ref<Event> = ref(props.event)
const canMark:Ref<boolean> = ref(props.canMark)
const canCreate:Ref<boolean> = ref(props.canCreate)
const canUpdate:Ref<boolean> = ref(props.canUpdate)
const canDelete:Ref<boolean> = ref(props.canDelete)
const searchTerm:Ref<string> = ref(props.searchTerm)
const colleges:Ref<Array<College>> = ref(props.colleges)
const searchCollegeId:Ref<number|null> = ref(props.searchCollegeId)
const enrollments:Ref<PaginatedModel<Enrollment>> = ref(props.enrollments)
const searchStatuses:Ref<Array<EnrollmentStatus>> = ref(props.searchStatuses)

function showFilters():void{
	closeDropdownList('enrollment-actions-dropdown')
	showModal('show-filter-modal')
}

function addStatus(status:EnrollmentStatus):void{
	searchStatuses.value.push(status)
}

function removeStatus(status:EnrollmentStatus):void{
	const index = searchStatuses.value.findIndex((s:EnrollmentStatus) => s === status)
	if(index > -1){
		searchStatuses.value.splice(index, 1)
	}
}

function filter():void{
	useForm({
		searchTerm: searchTerm.value,
		searchCollegeId: searchCollegeId.value,
		searchStatuses: searchStatuses.value.join(','),
	}).get(route('events.enrollments.index', {
		eventType: event.value.type,
		event: event.value.id,
	}))
}

function downloadBySearch():void{
	if(confirm('This will download the current list of enrollments. Do you wish to continue?')){
		window.open(route('events.enrollments.download.search', {
			event: event.value.id,
			term: searchTerm.value,
			searchCollegeId: searchCollegeId.value,
			searchStatuses: searchStatuses.value.join(','),
		}), '_self')
	}
}

function downloadAll():void{
	if(confirm('This will download a list of all enrollments. Do you wish to continue?')){
		window.location.href = route('events.enrollments.download.all', event.value.id)
	}
}

function remove(enrollmentId:number):void{
	const index = enrollments.value.data.findIndex((e:Enrollment) => e.id === enrollmentId)
	if(index > -1){
		enrollments.value.data.splice(index, 1)
	}
}
</script>